import React from 'react'
import { Modal, Typography, Space, Timeline, Tag, Alert } from 'antd'
import { 
  CheckCircleOutlined, 
  ThunderboltOutlined, 
  SafetyOutlined, 
  BarChartOutlined,
  InfoCircleOutlined 
} from '@ant-design/icons'

const { Title, Paragraph, Text } = Typography

interface HealthCheckInfoModalProps {
  open: boolean
  onClose: () => void
}

export const HealthCheckInfoModal: React.FC<HealthCheckInfoModalProps> = ({ open, onClose }) => {
  return (
    <Modal
      title={
        <Space>
          <InfoCircleOutlined style={{ color: '#1890ff' }} />
          <span>How Platform Health Monitoring Works</span>
        </Space>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={600}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%', marginTop: 12 }}>
        <Alert
          message="Ultra-Fast Zero-Lock Operational Telemetry"
          description="The Platform Status Pill gives real-time confidence without cluttering your admin experience or stressing backend databases."
          type="info"
          showIcon
        />

        <Timeline
          items={[
            {
              color: 'green',
              dot: <ThunderboltOutlined style={{ fontSize: 16 }} />,
              children: (
                <div>
                  <Text strong>1. Asynchronous Background Polling (Every 5s)</Text>
                  <Paragraph type="secondary" style={{ fontSize: 13, margin: '4px 0 0 0' }}>
                    A background daemon worker polls all 17 microservices' <Text code>/api/v1/health</Text> endpoints asynchronously using parallel HTTP connection pools (2s connect timeout).
                  </Paragraph>
                </div>
              ),
            },
            {
              color: 'blue',
              dot: <SafetyOutlined style={{ fontSize: 16 }} />,
              children: (
                <div>
                  <Text strong>2. Redis Cache & Atomic Locks (&lt; 5ms Response)</Text>
                  <Paragraph type="secondary" style={{ fontSize: 13, margin: '4px 0 0 0' }}>
                    Status snapshots are cached in Redis (<Text code>gateway:health:summary</Text>). Stale-While-Revalidate refresh uses an atomic lock to eliminate duplicate request stampedes.
                  </Paragraph>
                </div>
              ),
            },
            {
              color: 'gold',
              dot: <CheckCircleOutlined style={{ fontSize: 16 }} />,
              children: (
                <div>
                  <Text strong>3. Fail-Fast 3-State Circuit Breakers</Text>
                  <Paragraph type="secondary" style={{ fontSize: 13, margin: '4px 0 0 0' }}>
                    If a microservice drops, circuit breakers trip to <Tag color="warning">OPEN</Tag> state immediately, failing in &lt; 1ms without locking RoadRunner worker processes.
                  </Paragraph>
                </div>
              ),
            },
            {
              color: 'purple',
              dot: <BarChartOutlined style={{ fontSize: 16 }} />,
              children: (
                <div>
                  <Text strong>4. Production Observability via Grafana & Loki</Text>
                  <Paragraph type="secondary" style={{ fontSize: 13, margin: '4px 0 0 0' }}>
                    For deep CPU/memory metrics, slow query logs, and exception stack traces, refer to the production Grafana & Loki dashboards accessible via Tailscale VPN.
                  </Paragraph>
                </div>
              ),
            },
          ]}
        />
      </Space>
    </Modal>
  )
}
